import { AnimatePresence, motion } from "framer-motion";
import { Check, CircleDashed, Loader2, AlertTriangle } from "lucide-react";

import { cn } from "@/lib/utils";
import type {
  SectionProgress,
  SectionStatus,
  StreamingState,
} from "@/hooks/useStreamingDocgen";

/**
 * Sprint B — W2.
 *
 * Replaces the dead 30-second spinner from Ring 2B's docgen flow with
 * a row-by-row progress list. Pre-seeds with the 11 LLM sections + 2
 * deterministic tables so the user sees the full pipeline before any
 * SSE event arrives — running sections animate in, completed sections
 * tick green, the slow ``konstrukcija_gradevine`` quality-model row
 * stays "running" while the fast ones finish.
 *
 * The deterministic tables are surfaced as static "done" rows because
 * they're fully synchronous and complete before the prose pool starts
 * (see :func:`generate_project_documents`); they only appear in the
 * list to give the user a complete inventory of what's being produced.
 */

// Order matches ``permitpilot.docgen.document_assembler.PROSE_GENERATORS``.
// Keep this in sync — the streaming endpoint also keys off these IDs and
// drift would surface as "untitled" rows in the UI.
const EXPECTED_PROSE: Array<{ id: string; title: string }> = [
  { id: "opis_zahvata", title: "Opis zahvata" },
  { id: "planska_dokumentacija", title: "Važeća planska dokumentacija" },
  { id: "smjestaj_gradevine", title: "Smještaj građevine na čestici" },
  { id: "oblikovanje_gradevine", title: "Oblikovanje građevine" },
  { id: "velicina_gradevine", title: "Veličina i površina građevine" },
  { id: "konstrukcija_gradevine", title: "Konstrukcija građevine" },
  { id: "obrada_zidova", title: "Obrada zidova" },
  { id: "podovi", title: "Podovi" },
  { id: "stolarija", title: "Stolarija" },
  { id: "grijanje_hladenje", title: "Grijanje i hlađenje" },
  { id: "tlocrtna_dispozicija", title: "Tlocrtna dispozicija" },
];

const DETERMINISTIC_TABLES: Array<{ id: string; title: string }> = [
  { id: "iskaz_otvora", title: "Iskaz otvora (automatska tablica)" },
  { id: "iskaz_materijala", title: "Iskaz materijala (automatska tablica)" },
];

interface GeneratingProgressProps {
  state: StreamingState;
}

export function GeneratingProgress({ state }: GeneratingProgressProps) {
  const rows = mergeRows(state);
  const doneCount = rows.filter((r) => r.status === "done").length;
  const totalCount = rows.length;
  const isStreaming = state.status === "streaming";
  const isDone = state.status === "done";

  return (
    <div className="rounded-lg border border-stone-200 bg-white p-6 shadow-sm">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="text-[1.05rem] font-bold text-stone-800">
            Generiranje dokumentacije
          </h2>
          <p className="text-[0.85rem] text-stone-500">
            {isStreaming
              ? "LLM sekcije se generiraju paralelno; deterministički iskazi su gotovi instantno."
              : isDone
                ? "Sve sekcije su generirane."
                : "Pokretanje pipelinea..."}
          </p>
        </div>
        <div className="text-right">
          <div className="text-[0.95rem] font-bold text-stone-700">
            {doneCount} / {totalCount}
          </div>
          <div className="text-[0.75rem] text-stone-500">
            {state.elapsedSeconds.toFixed(1)} s
          </div>
        </div>
      </div>

      <div className="mb-4 h-1.5 w-full overflow-hidden rounded-full bg-stone-100">
        <div
          className={cn(
            "h-full rounded-full transition-[width] duration-300",
            isDone ? "bg-emerald-500" : "bg-ind-600",
          )}
          style={{ width: `${(doneCount / totalCount) * 100}%` }}
        />
      </div>

      <ul className="divide-y divide-stone-100">
        {rows.map((row) => (
          <ProgressRow key={row.section_id} row={row} />
        ))}
      </ul>

      {state.status === "error" && state.error && (
        <div
          role="alert"
          className="mt-4 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-[0.85rem] text-red-700"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <div>
            <div className="font-medium">Greška pri generiranju</div>
            <div className="text-[0.8rem] text-red-600">{state.error}</div>
          </div>
        </div>
      )}
    </div>
  );
}

interface ProgressRowProps {
  row: SectionProgress;
}

function ProgressRow({ row }: ProgressRowProps) {
  const issueCount =
    (row.validation_errors?.length ?? 0) +
    (row.validation_warnings?.length ?? 0);
  return (
    <motion.li
      // Sprint B — W6. Stagger-in once per status change. Subtle 200 ms
      // ease keeps the list calm during the burst of ``section_completed``
      // events that arrive near the end of generation.
      layout
      initial={{ opacity: 0.5, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
      className={cn(
        "flex items-center justify-between gap-3 py-2.5 text-[0.88rem]",
        row.status === "queued" && "text-stone-400",
        row.status === "running" && "text-stone-700",
        row.status === "done" && "text-stone-800",
        row.status === "error" && "text-red-700",
      )}
    >
      <div className="flex items-center gap-3">
        <AnimatePresence mode="wait" initial={false}>
          <motion.span
            key={row.status}
            initial={{ scale: 0.6, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.6, opacity: 0 }}
            transition={{ duration: 0.15, ease: "easeOut" }}
            className="inline-flex"
          >
            <StatusIcon status={row.status} />
          </motion.span>
        </AnimatePresence>
        <span>{row.title}</span>
      </div>
      {issueCount > 0 && (
        <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[0.7rem] font-medium uppercase tracking-wide text-amber-700">
          {issueCount} {issueCount === 1 ? "napomena" : "napomena"}
        </span>
      )}
    </motion.li>
  );
}

function StatusIcon({ status }: { status: SectionStatus }) {
  switch (status) {
    case "queued":
      return <CircleDashed className="h-4 w-4 text-stone-300" aria-hidden />;
    case "running":
      return <Loader2 className="h-4 w-4 animate-spin text-ind-600" aria-hidden />;
    case "done":
      return <Check className="h-4 w-4 text-emerald-600" aria-hidden />;
    case "error":
      return <AlertTriangle className="h-4 w-4 text-red-600" aria-hidden />;
  }
}

/**
 * Merge the live SSE-driven section state with the pre-seeded list of
 * expected sections. Sections we have live state for take precedence;
 * others appear as ``queued`` so the layout doesn't reflow as events
 * arrive. Deterministic tables are flagged ``done`` once the pipeline
 * is finished (they're materialised inline and never streamed).
 */
function mergeRows(state: StreamingState): SectionProgress[] {
  const isDone = state.status === "done";
  const rows: SectionProgress[] = [];
  for (const expected of EXPECTED_PROSE) {
    const live = state.sections[expected.id];
    if (live) {
      rows.push(live);
    } else {
      rows.push({
        section_id: expected.id,
        title: expected.title,
        status: isDone ? "done" : "queued",
      });
    }
  }
  for (const table of DETERMINISTIC_TABLES) {
    rows.push({
      section_id: table.id,
      title: table.title,
      status: isDone ? "done" : "queued",
    });
  }
  // Surface any extra sections the backend streams that we didn't
  // anticipate (forward-compat) at the bottom of the list.
  for (const sid of state.sectionOrder) {
    if (EXPECTED_PROSE.find((e) => e.id === sid)) continue;
    if (!state.sections[sid]) continue;
    rows.push(state.sections[sid]);
  }
  return rows;
}
